/* Offline-first sync engine: outbox queue + snapshot cache. Server is truth. */
import { useEffect, useState } from "react";
import { api } from "./api";
import { dbGet, dbSet, outboxAll, outboxDelete, outboxPut } from "./localdb";
import { isDataSaver } from "./dataSaver";

let barId = null;
const state = { online: navigator.onLine, pending: 0, syncing: false, dead: 0 };
const statusSubs = new Set();
const snapSubs = new Set();

function emit() {
  statusSubs.forEach((f) => f({ ...state }));
}

function emitSnapshot(s) {
  snapSubs.forEach((f) => f(s));
}

async function refreshPending() {
  const all = barId ? await outboxAll() : [];
  state.pending = all.filter((e) => !e.dead).length;
  state.dead = all.filter((e) => e.dead).length;
  emit();
}

export function subscribeStatus(f) {
  statusSubs.add(f);
  f({ ...state });
  return () => statusSubs.delete(f);
}

export function subscribeSnapshot(f) {
  snapSubs.add(f);
  return () => snapSubs.delete(f);
}

export async function initSync(id) {
  if (barId === id) return;
  barId = id;
  await refreshPending();
  if (state.online) {
    flush();
    hydrate();
  }
}

export async function hydrate() {
  if (!barId || !state.online) return;
  try {
    const { data } = await api.get(`/sync/bars/${barId}/snapshot`);
    await dbSet(`snap:${barId}`, data);
    emitSnapshot(data);
  } catch (e) {
    console.debug("snapshot hydrate skipped (offline or transient)", e?.message);
  }
}

export async function getSnapshot() {
  return barId ? await dbGet(`snap:${barId}`) : null;
}

export async function mutateSnapshot(fn) {
  const s = await getSnapshot();
  if (!s) return null;
  fn(s);
  await dbSet(`snap:${barId}`, s);
  emitSnapshot(s);
  return s;
}

export function tempId(prefix = "tmp") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

let seqCounter = 0;

export async function enqueue(type, payload) {
  const ev = {
    event_id: window.crypto?.randomUUID ? window.crypto.randomUUID() : tempId("ev"),
    type,
    payload,
    device_ts: new Date().toISOString(),
    seq: Date.now() * 1000 + (seqCounter = (seqCounter + 1) % 1000),
    attempts: 0,
  };
  await outboxPut(ev);
  await refreshPending();
  if (state.online) flush();
  return ev;
}

let flushing = false;

export async function flush() {
  if (flushing || !barId || !navigator.onLine) return;
  const all = (await outboxAll()).filter((e) => !e.dead).sort((a, b) => (a.seq || 0) - (b.seq || 0));
  if (!all.length) return;
  flushing = true;
  state.syncing = true;
  emit();
  try {
    const events = all.map(({ attempts, dead, error, seq, ...e }) => e);
    const { data } = await api.post(`/sync/bars/${barId}/events`, { events });
    for (const r of data.results) {
      if (r.status === "applied" || r.status === "duplicate") {
        await outboxDelete(r.event_id);
      } else {
        const ev = all.find((e) => e.event_id === r.event_id);
        if (ev) {
          ev.attempts = (ev.attempts || 0) + 1;
          ev.error = r.error;
          if (ev.attempts >= 5) ev.dead = true;
          await outboxPut(ev);
        }
      }
    }
    await dbSet(`snap:${barId}`, data.snapshot);
    emitSnapshot(data.snapshot);
  } catch (e) {
    console.debug("sync flush failed — events stay queued, retried on next tick", e?.message);
  }
  flushing = false;
  state.syncing = false;
  await refreshPending();
}

export async function deadEvents() {
  if (!barId) return [];
  return (await outboxAll()).filter((e) => e.dead);
}

export async function retryDead() {
  const all = await outboxAll();
  for (const e of all.filter((x) => x.dead)) {
    e.dead = false;
    e.attempts = 0;
    await outboxPut(e);
  }
  await refreshPending();
  flush();
}

window.addEventListener("online", () => {
  state.online = true;
  emit();
  flush();
  hydrate();
});
window.addEventListener("offline", () => {
  state.online = false;
  emit();
});
let flushTick = 0;
setInterval(() => {
  flushTick += 1;
  if (!navigator.onLine) return;
  if (isDataSaver() && flushTick % 3 !== 0) return;
  flush();
}, 30000);

export function useSyncStatus() {
  const [s, setS] = useState({ ...state });
  useEffect(() => subscribeStatus(setS), []);
  return s;
}

export function useOnline() {
  return useSyncStatus().online;
}
